import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import AddRouteComponentProp from "./common/edit.route/add.route.component.prop";
import CountriesAndSiteGroups from "./countries.and.site.groups";
import EditRoute from "./common/edit.route";
import Header from "../common/header";
import LinearProgress from "@material-ui/core/LinearProgress";
import OverallParameters from "./overall.parameters";
import Paper from "../common/paper";
import Review from "./review";
import Simulation from "./simulation";
import styled from "styled-components";
import { useQuery } from "../../app/hooks";
import { useStudyPlan } from "../../app/contexts/study.context";
import { useUser } from "../../app/contexts/user.context";
import { validateStudyPlan } from "./validations/validate.study.plan";

const StudyPlan = () => {
  const { planId, id } = useParams();
  const query = useQuery();
  const scenario = query.get("s");
  const sequentialCohort = query.get("seq");
  const {
    saveLoading,
    loadingPlan,
    fetchStudyPlan,
    seqCohortsStatus,
    fetchCohortStudyPlan,
    createNewPlan,
    studyPlan,
    study,
  } = useStudyPlan();

  const { studyPlanType, studyPlanTypeDetail } = studyPlan || {};
  const [formKey, setFormKey] = useState(null);
  const [validationResult, setValidationResult] = useState({ errors: {}, warnings: {} });
  const initialValues = studyPlan || {};
  const { isReadOnlyUser, setURLStudyId } = useUser();

  const handleValidation = (values) => {
    // Use values from Formik instead of studyPlan context to get the most recent changes
    const currentStudyPlan = {
      ...studyPlan,
      ...values,
    };
    
    const result = validateStudyPlan(
      currentStudyPlan,
      studyPlanType,
      study,
    );
    
    setValidationResult(result);
    
    // Formik expects only errors for form validation
    return result.errors || {};
  };

  useEffect(() => {
    setURLStudyId(id);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      studyPlan &&
      studyPlanType === 2 &&
      studyPlanTypeDetail === "Cohort (D)" &&
      seqCohortsStatus === "idle"
    ) {
      fetchCohortStudyPlan(
        planId,
        process.env.REACT_APP_USERNAME,
        process.env.REACT_APP_PASSWORD,
      );
    }
    // eslint-disable-next-line
  }, [studyPlan, seqCohortsStatus, studyPlanType]);

  useEffect(() => {
    if (!sequentialCohort) createNewPlan();
    if (planId.includes("new-plan_")) {
      createNewPlan(scenario, sequentialCohort);
    } else {
      fetchStudyPlan(
        planId,
        process.env.REACT_APP_USERNAME,
        process.env.REACT_APP_PASSWORD,
      );
    }
    // eslint-disable-next-line
  }, [planId]);

  useEffect(() => {
    if (studyPlan && !formKey) setFormKey(new Date().getTime());
  }, [studyPlan, formKey]);

  if (loadingPlan || saveLoading) {
    return (
      <Container>
        <Paper style={{ paddingTop: "1em" }}>
          <LinearProgress />
        </Paper>
      </Container>
    );
  }

  return (
    <Container>
      <Paper>
        <Formik
          key={formKey}
          validate={handleValidation}
          initialValues={{ ...initialValues, updated: new Date().getTime() }}
        >
          {({ errors, values, touched, setValues }) => (
            <Form>
              <HeaderContainer>
                <Header />
              </HeaderContainer>
              <Routes>
                {/* Redirect read-only users from restricted routes */}
                {isReadOnlyUser && (
                  <Route
                    index
                    element={
                      <Navigate
                        to={`/ctpo/study-plans/${id}/${planId}/review`}
                        replace
                      />
                    }
                  />
                )}
                {isReadOnlyUser && (
                  <Route
                    path="countries-and-site-groups"
                    element={
                      <Navigate
                        to={`/ctpo/study-plans/${id}/${planId}/review`}
                        replace
                      />
                    }
                  />
                )}

                {/* Normal routes for non-read-only users */}
                {!isReadOnlyUser && (
                  <Route
                    index
                    element={
                      <EditRoute>
                        <OverallParameters
                          values={values}
                          errors={errors}
                          warnings={validationResult.warnings}
                          setValues={setValues}
                          step={1}
                        />
                      </EditRoute>
                    }
                  />
                )}
                {!isReadOnlyUser && (
                  <Route
                    path="countries-and-site-groups"
                    element={
                      <EditRoute>
                        <CountriesAndSiteGroups
                          values={values}
                          errors={errors}
                          warnings={validationResult.warnings}
                          setValues={setValues}
                          touched={touched}
                          step={2}
                        />
                      </EditRoute>
                    }
                  />
                )}

                {/* Always accessible routes */}
                <Route
                  path="review"
                  element={
                    <AddRouteComponentProp
                      component={Review}
                      additionalProps={{
                        values,
                        errors,
                        warnings: validationResult.warnings,
                        setValues,
                      }}
                    />
                  }
                />
                <Route path="simulation" element={<Simulation />} />
              </Routes>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default StudyPlan;

const Container = styled.div`
  min-width: 1300px;
  width: 100%;
  min-height: 100vh;
`;

const HeaderContainer = styled.div`
  position: sticky;
  top: 3.8em;
  z-index: 110;
  border-radius: 4px;
  background: var(--White);
  padding: 1em 0 0 0;
`;
